import React from 'react';
import {
  Card,
  Stack,
  Text,
  AutoSavedSwitcherControl,
  AutoSavedSelectControl,
  AutoSavedRadioButtonGroupControl,
  Spinner,
} from '@piwikpro/ui-components';
import { withInject } from '@piwikpro/platform';

import {
  TrackerSettings,
  TrackerSettingsResponse,
} from '../../../../TrackerSettings';
import FailedMessage from '../../FailMessage';

export interface IPAnonimizationProps {
  settings: TrackerSettingsResponse
  isLoading: boolean
  failed: boolean
  trackerSettings: TrackerSettings
  t: Function
}

enum anonymizeVisitorIpLevels {
  noAnonymize = 0,
  level1 = 1,
  level2 = 2,
  level3 = 3,
  level4 = 4,
}

export class IPAnonimization extends React.Component<IPAnonimizationProps> {
  private readonly geolocationLevels: Array<any>;

  constructor(props: IPAnonimizationProps) {
    super(props);
    const { t } = props;

    this.handleUpdateSettings = this.handleUpdateSettings.bind(this);

    this.geolocationLevels = [
      {
        id: 'none',
        value: 'none',
        name: t('trackerSettings.privacy.anonimization.useIps.gelocation.none'),
      },
      {
        id: 'City',
        value: 'City',
        name: t('trackerSettings.privacy.anonimization.useIps.gelocation.city'),
      },
      {
        id: 'Region',
        value: 'Region',
        name: t('trackerSettings.privacy.anonimization.useIps.gelocation.region'),
      },
      {
        id: 'Country',
        value: 'Country',
        name: t('trackerSettings.privacy.anonimization.useIps.gelocation.country'),
      },
    ];
  }

  async handleUpdateSettings(values: any, {
    setErrors,
    setSubmitting,
    setFieldValue,
  }: any) {
    const { trackerSettings, settings } = this.props;

    try {
      if (values.anonymize_visitor_ip_level !== undefined) {
        let anonymizeIpLevel;

        switch (values.anonymize_visitor_ip_level) {
          case false:
            anonymizeIpLevel = anonymizeVisitorIpLevels.noAnonymize;
            break;
          case true:
            anonymizeIpLevel = anonymizeVisitorIpLevels.level2;
            break;
          default:
            anonymizeIpLevel = Number(values.anonymize_visitor_ip_level);
        }

        if (anonymizeIpLevel !== settings.attributes.anonymize_visitor_ip_level) {
          await trackerSettings.updateSettings({
            anonymize_visitor_ip_level: anonymizeIpLevel,
          });
        }
      } else {
        await trackerSettings.updateSettings(values);
      }
    } catch (err: any) {
      setFieldValue(
        Object.keys(values)[0],
        (settings.attributes as any)[Object.keys(values)[0]],
        false,
      );
      setErrors(err.errors);
    }

    setSubmitting(false);
  }

  renderIpOptions() {
    const {
      settings,
      failed,
      t,
    } = this.props;

    return (
      <Card>
        <Card.Section subdued>
          <Stack wrap verticalAlignment="center">
            <Stack.Item fill>
              <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">
                {t('trackerSettings.privacy.anonimization.anonimizeIdAdresses.title')}
              </Text>
              <Text as="p">
                {t('trackerSettings.privacy.anonimization.anonimizeIdAdresses.description')}
              </Text>
            </Stack.Item>
            {!failed && (
              <Stack.Item>
                <AutoSavedSwitcherControl
                  autoFocus
                  id="user-profile-tracker-settings-anonimize-ips-form"
                  initialValue={settings.attributes.anonymize_visitor_ip_level !== 0}
                  name="anonymize_visitor_ip_level"
                  onSubmit={this.handleUpdateSettings}
                />
              </Stack.Item>
            )}
          </Stack>
        </Card.Section>
        {failed ? <Card.Section><FailedMessage /></Card.Section>
          : (
            <>
              <Card.Section subdued>
                <Stack vertical spacing="narrow">
                  <Stack.Item>
                    <AutoSavedRadioButtonGroupControl
                      name="anonymize_visitor_ip_level"
                      initialValue={
                        settings.attributes.anonymize_visitor_ip_level
                      || anonymizeVisitorIpLevels.level2
                      }
                      onSubmit={this.handleUpdateSettings}
                      options={[
                        {
                          id: '1',
                          value: String(anonymizeVisitorIpLevels.level1),
                          label: t('trackerSettings.privacy.anonimization.firstOption'),
                          disabled: !settings.attributes.anonymize_visitor_ip_level,
                        },
                        {
                          id: '2',
                          value: String(anonymizeVisitorIpLevels.level2),
                          label: t('trackerSettings.privacy.anonimization.secondOption'),
                          disabled: !settings.attributes.anonymize_visitor_ip_level,
                        },
                        {
                          id: '3',
                          value: String(anonymizeVisitorIpLevels.level3),
                          label: t('trackerSettings.privacy.anonimization.thirdOption'),
                          disabled: !settings.attributes.anonymize_visitor_ip_level,
                        },
                        {
                          id: '4',
                          value: String(anonymizeVisitorIpLevels.level4),
                          label: t('trackerSettings.privacy.anonimization.fourthOption'),
                          disabled: !settings.attributes.anonymize_visitor_ip_level,
                        },
                      ]}
                    />
                  </Stack.Item>
                  <Stack.Item>
                    <Text as="p" size={12} color="InkLighter" dataId="Caption">
                      {t('trackerSettings.privacy.anonimization.options.description')}
                    </Text>
                  </Stack.Item>
                </Stack>
              </Card.Section>
              <Card.Section subdued>
                <Stack wrap verticalAlignment="center">
                  <Stack.Item fill>
                    <Text
                      as="h3"
                      size={16}
                      weight={700}
                      wrapType="noWrapAndCutText"
                      dataId="Header.small"
                      {...(!settings.attributes.anonymize_visitor_ip_level ? { color: 'InkLightest' } : {})}
                    >
                      {t('trackerSettings.privacy.anonimization.useIps.title')}
                    </Text>
                    <Text
                      as="p"
                      {...(settings.attributes.anonymize_visitor_ip_level ? {} : { color: 'InkLighter' })}
                    >
                      {t('trackerSettings.privacy.anonimization.useIps.description')}
                    </Text>
                  </Stack.Item>
                  <Stack.Item>
                    <div style={{ minWidth: '152px' }}>
                      <AutoSavedSelectControl
                        id="user-profile-tracker-settings-use-anonimized-ips-form"
                        initialValue={
                          settings.attributes.anonymize_visitor_geolocation_level
                        }
                        name="anonymize_visitor_geolocation_level"
                        options={[{ id: 0, name: '', items: this.geolocationLevels }]}
                        disabled={!settings.attributes.anonymize_visitor_ip_level}
                        onSubmit={this.handleUpdateSettings}
                      />
                    </div>
                  </Stack.Item>
                </Stack>
              </Card.Section>
            </>
          )}
      </Card>
    );
  }

  render() {
    const {
      isLoading,
      settings,
      t,
    } = this.props;

    if (isLoading) {
      return <Spinner />;
    }

    return (
      <Card>
        <Card.Section>
          <Stack wrap verticalAlignment="center">
            <Stack.Item fill>
              <Stack vertical spacing="narrow">
                <Stack.Item>
                  <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">
                    {t('apps.details.settings.ip-tracking.title')}
                  </Text>
                </Stack.Item>
                <Stack.Item>
                  <Text as="p">
                    {t('apps.details.settings.ip-tracking.description')}
                  </Text>
                </Stack.Item>
              </Stack>
            </Stack.Item>
            <Stack.Item>
              <AutoSavedSwitcherControl
                id="app-settings-tracker-settings-ip-tracking"
                initialValue={settings.attributes.set_ip_tracking}
                name="set_ip_tracking"
                onSubmit={this.handleUpdateSettings}
              />
            </Stack.Item>
            {!settings.attributes.set_ip_tracking || (
              <Stack.Item>{this.renderIpOptions()}</Stack.Item>
            )}
          </Stack>
        </Card.Section>
      </Card>
    );
  }
}

export default withInject({
  trackerSettings: 'TrackerSettingsCrate.tracker',
})(IPAnonimization);
